import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { StatusPill } from '@/components/ui/kit';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu';
import { WorkspaceIcon } from '../icons';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy, { type StorefrontCopyKey } from '@/i18n/ns/storefront';
import { useAgentFeatures } from '../agent-features';
import { useWorkspace } from '../context';
import { emptyTurnContext } from '../types';
import { EmptyState, ErrorState, LoadingState, PageHeading } from '../primitives';
import {
  useStorefrontApi,
  type OverviewScope,
  type StoreDetail,
  type StoreMarket,
  type StoreSummary,
  siteUnchecked,
} from './api';
import { ConversationsTab } from './conversations-tab';
import { InstallTab } from './install-tab';
import { OverviewTab } from './overview-tab';
import { MarketFilter } from './market-filter';
import { SetupTab } from './setup-tab';
import { runScopeText, storeLine } from './store-line';
import { weekAsk, type AskChat } from './ask-chat';
import { failureText, useRemote } from './use-storefront';
import { CompanyRequestError } from '@/company/api';
import { WidgetTab } from './widget-tab';

type Tab = 'overview' | 'conversations' | 'widget' | 'install' | 'setup';

const hashParam = (name: string, hash = location.hash) =>
  new URLSearchParams(hash.split('?')[1] ?? '').get(name);

/** Where the page is: one store or all of them (`all`), a market, a tab. Kept in the address. */
interface Place {
  store: string | null;
  market: string;
  tab: string | null;
}
export const placeFromAddress = (hash = location.hash): Place => ({
  store: hashParam('store', hash),
  market: hashParam('market', hash) ?? 'all',
  tab: hashParam('tab', hash),
});
/**
 * What an address change means for the page. A canvas opened over it keeps the page where it was;
 * that canvas closing (the address loses `canvas=` and with it the place) puts the place back.
 * Anything else, such as Back, Forward, a link or the Storefront item in the menu, goes there.
 */
export function placeAfter(
  before: string,
  now: string,
  current: Place,
): { place: Place; restore: boolean } | null {
  if (!now.startsWith('#/workspace/storefront') || now.includes('canvas=')) return null;
  const next = placeFromAddress(now);
  const bare = !next.store && !next.tab && next.market === 'all';
  if (before.includes('canvas=') && bare) return { place: current, restore: true };
  return { place: next, restore: false };
}
/**
 * Writes the place into the address. Another store is a new entry, so Back returns to the store
 * (or every store) before it; a tab or a market changes the entry in place. A canvas open over the
 * page keeps its own address.
 */
export function writePlace(place: Place, push: boolean) {
  if (!location.hash.startsWith('#/workspace/storefront') || location.hash.includes('canvas='))
    return;
  const params = new URLSearchParams();
  if (place.store) params.set('store', place.store);
  if (place.market !== 'all') params.set('market', place.market);
  if (place.tab) params.set('tab', place.tab);
  const query = params.toString();
  const hash = `#/workspace/storefront${query ? `?${query}` : ''}`;
  if (location.hash === hash) return;
  const url = `${location.pathname}${location.search}${hash}`;
  if (push) history.pushState(history.state, '', url);
  else history.replaceState(history.state, '', url);
}

/**
 * Storefront: one shopping assistant per store, set up, tried, installed and followed here. With more
 * than one, the page opens on all of them; the title switches between all and each one, and a store
 * with several markets can be narrowed to one. Both pickers hide when there is only one.
 */
export function StorefrontPage() {
  const features = useAgentFeatures();
  const copy = useNamespaceCopy(storefrontCopy);
  const enabled = features?.storefront === true;
  const api = useStorefrontApi();
  const stores = useRemote(enabled ? () => api.list() : null, String(enabled));
  // `#/workspace/storefront?store=st_…` opens that store (`store=all` every store); otherwise all of
  // them when there are several, else the one there is. `&tab=` and `&market=` open those.
  const [place, setPlace] = useState<Place>(() => placeFromAddress());
  const current = useRef(place);
  current.current = place;
  const seen = useRef(location.hash);
  // Back and Forward move between the places the page wrote into the address; a canvas over the
  // page leaves it where it was.
  useEffect(() => {
    const read = () => {
      const before = seen.current;
      seen.current = location.hash;
      const next = placeAfter(before, location.hash, current.current);
      if (!next) return;
      if (next.restore) writePlace(next.place, false);
      else setPlace(next.place);
    };
    window.addEventListener('popstate', read);
    window.addEventListener('hashchange', read);
    return () => {
      window.removeEventListener('popstate', read);
      window.removeEventListener('hashchange', read);
    };
  }, []);
  const go = (next: Partial<Place>, push: boolean) => {
    const merged = { ...place, ...next };
    setPlace(merged);
    writePlace(merged, push);
  };
  const storeId = place.store;
  const market = place.market;
  const setMarket = (code: string) => go({ market: code }, false);
  const setTab = (tab: Tab) => go({ tab }, false);
  const [adding, setAdding] = useState(false);
  // A store just added shows at once, before (or without) the list reloading.
  const [created, setCreated] = useState<string | null>(null);
  const list = stores.data?.stores ?? [];
  const several = list.length > 1;
  const activeId =
    storeId === 'all' && several
      ? 'all'
      : storeId && (storeId === created || list.some((store) => store.id === storeId))
        ? storeId
        : several
          ? 'all'
          : (list[0]?.id ?? null);
  // Another store keeps the tab the person is on (Conversations stays Conversations).
  const pick = (id: string, nextMarket = 'all') => {
    go({ store: id, market: nextMarket }, true);
    setAdding(false);
  };
  return (
    <div className="sf-page">
      {!features ? (
        <LoadingState label={copy.loading} />
      ) : !enabled ? (
        <>
          <PageHeading title={copy.pageTitle} />
          <EmptyState icon="store" title={copy.disabledTitle} description={copy.disabledBody} />
        </>
      ) : stores.loading && !stores.data ? (
        <LoadingState label={copy.loading} />
      ) : stores.error && !stores.data ? (
        <ErrorState message={copy.loadFailed} onRetry={stores.refresh} />
      ) : activeId === 'all' && !adding ? (
        <AllView
          stores={list}
          market={market}
          onMarket={setMarket}
          tab={place.tab}
          onTab={setTab}
          onPick={pick}
          onAdd={() => setAdding(true)}
        />
      ) : activeId && !adding ? (
        <StoreView
          key={activeId}
          storeId={activeId}
          stores={list}
          market={market}
          onMarket={setMarket}
          tab={place.tab}
          onTab={setTab}
          onPick={pick}
          onAdd={() => setAdding(true)}
          onChanged={stores.refresh}
        />
      ) : (
        <StartStore
          onStarted={(id) => {
            setCreated(id);
            pick(id);
            stores.refresh();
          }}
          onUncertain={stores.refresh}
          onCancel={activeId ? () => setAdding(false) : undefined}
        />
      )}
    </div>
  );
}

/**
 * The words for a start that failed, and whether the store list should be read again. A start whose
 * answer was lost (a 5xx) may have begun: the store is kept for it, so it shows in the list and a
 * retry reaches the same store. That is our side, never the address.
 */
export function startFailure(
  failure: unknown,
  copy: Record<StorefrontCopyKey, string>,
): { text: string; refresh: boolean } {
  if (failure instanceof CompanyRequestError && failure.status >= 500)
    return { text: copy.errorReadUnavailable, refresh: true };
  return { text: failureText(failure, copy, copy.startFailed), refresh: false };
}

function StartStore({
  onStarted,
  onUncertain,
  onCancel,
}: {
  onStarted: (id: string) => void;
  /** A start that may have begun: the page reads its store list again. */
  onUncertain?: () => void;
  onCancel?: () => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const api = useStorefrontApi();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!url.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      const { store } = await api.create(normalizeUrl(url));
      onStarted(store.id);
    } catch (failure) {
      const { text, refresh } = startFailure(failure, copy);
      setError(text);
      setBusy(false);
      if (refresh) onUncertain?.();
    }
  };
  return (
    <section className="sf-start">
      <span className="sf-start-eyebrow">{copy.pageTitle}</span>
      <h1>{copy.startTitle}</h1>
      <p>{copy.startBody}</p>
      <form className="sf-start-form" onSubmit={submit}>
        <label htmlFor="sf-url">{copy.urlLabel}</label>
        <div className="sf-start-row">
          <input
            id="sf-url"
            type="text"
            inputMode="url"
            autoComplete="url"
            autoCapitalize="none"
            spellCheck={false}
            placeholder={copy.urlPlaceholder}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            required
          />
          <button className="ws-button primary" type="submit" disabled={busy || !url.trim()}>
            {busy ? copy.starting : copy.startButton}
          </button>
        </div>
        {error && (
          <p className="sf-start-error" role="alert">
            {error}
          </p>
        )}
      </form>
      {onCancel && (
        <button type="button" className="ws-text-button sf-start-back" onClick={onCancel}>
          {copy.backToStores}
        </button>
      )}
    </section>
  );
}

/** "apotea.se" works as well as "https://www.apotea.se"; the server checks the rest. */
function normalizeUrl(value: string): string {
  const text = value.trim();
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`;
}

function StoreView({
  storeId,
  stores,
  market,
  onMarket,
  tab,
  onTab: setTab,
  onPick,
  onAdd,
  onChanged,
}: {
  storeId: string;
  stores: StoreSummary[];
  market: string;
  onMarket: (code: string) => void;
  tab: string | null;
  onTab: (tab: Tab) => void;
  onPick: (id: string, market?: string) => void;
  onAdd: () => void;
  /** The store's status changed here (publish, unpublish): the page's list follows. */
  onChanged: () => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const ws = useWorkspace();
  const api = useStorefrontApi();
  const [polling, setPolling] = useState(true);
  const remote = useRemote(() => api.get(storeId).then((answer) => answer.store), storeId, polling);
  const store = remote.data;
  // Poll while a run is going, also when a live store is being read again.
  const runActive = Boolean(
    store?.run && !['ready', 'failed', 'cancelled', 'held'].includes(store.run.status),
  );
  useEffect(
    () => setPolling(!store || store.status === 'reading' || runActive),
    [store?.status, runActive],
  );
  const tabs: Tab[] =
    store?.status === 'live'
      ? ['overview', 'conversations', 'widget', 'install', 'setup']
      : ['setup', 'widget', 'install'];
  // The page's tab when this store has it (`&tab=install` from the canvas's Publish link too),
  // else the store's first.
  const active: Tab = tabs.find((name) => name === tab) ?? tabs[0];
  // On a phone the tabs scroll sideways: the one open is brought into view, never left cut at the edge.
  useEffect(() => {
    document
      .getElementById(`sf-tab-${active}`)
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [active, store?.status]);
  if (!store) {
    return remote.error ? (
      <ErrorState message={copy.loadFailed} onRetry={remote.refresh} />
    ) : (
      <LoadingState label={copy.loading} />
    );
  }
  const label: Record<Tab, string> = {
    overview: copy.tabOverview,
    conversations: copy.tabConversations,
    widget: copy.tabWidget,
    install: copy.tabInstall,
    setup: copy.tabSetup,
  };
  const onTabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next = tabs[(tabs.indexOf(active) + step + tabs.length) % tabs.length];
    setTab(next);
    requestAnimationFrame(() => document.getElementById(`sf-tab-${next}`)?.focus());
  };
  const notify = (text: string) => ws.notify(text, null, 'error');
  const ask = (prompt: string) => ws.send(prompt, undefined, false, null, { newChat: true });
  // Asking about conversations attaches them as a reference: Chat reads them itself.
  const askAbout = ({ text, reference }: AskChat) =>
    ws.send(text, { ...emptyTurnContext(), storefrontConversations: reference }, false, null, {
      newChat: true,
    });
  // Open the current design immediately, just as a Skill opens beside its chat.
  // The neutral request remains a draft: inspecting never starts a turn or changes the design.
  const design = () =>
    ws.send(
      '',
      { ...emptyTurnContext(), storefrontTarget: { store: store.id } },
      false,
      { kind: 'storefront', id: store.id },
      { newChat: true },
    );
  const knowledge = () =>
    ask(formatCopy(copy.knowledgePrompt, { name: store.name, domain: store.domain }));
  const profile = () =>
    ask(formatCopy(copy.profilePrompt, { name: store.name, domain: store.domain }));
  const pages = () => ask(formatCopy(copy.pagesPrompt, { name: store.name, domain: store.domain }));
  const updatePages = () =>
    ask(formatCopy(copy.pagesUpdatePrompt, { name: store.name, domain: store.domain }));
  const addMarket = () =>
    ask(formatCopy(copy.addMarketPrompt, { name: store.name, domain: store.domain }));
  // A stopped read is steered in Chat: it reads the run itself, suggests what to tell the read, then
  // checks again or reads again with that hint, as little of the store as it can.
  const fixRead = (step: string | null) =>
    ask(
      step
        ? formatCopy(copy.fixReadPromptStep, { name: store.name, domain: store.domain, step })
        : formatCopy(copy.fixReadPrompt, { name: store.name, domain: store.domain }),
    );
  const markets = store.markets && store.markets.length > 1 ? store.markets : [];
  const onSite = store.install.domains.some((domain) => domain.lineFound);
  // Added to the site is the next step only when the check could look; a blocked check says nothing.
  const unchecked = siteUnchecked(store.install.domains);
  const scope: OverviewScope = {
    store: store.id,
    market: markets.some((entry) => entry.code === market) ? market : 'all',
  };
  const marketName = markets.find((entry) => entry.code === scope.market)?.name ?? null;
  const scopeName = marketName
    ? formatCopy(copy.scopeIn, { assistant: store.name, market: marketName })
    : store.name;
  // Chat reads the week itself (storefront_conversations): fixed words, the week attached.
  const marketPicked = markets.find((entry) => entry.code === scope.market) ?? null;
  const askWeek = () => askAbout(weekAsk(copy, scope, store.name, marketPicked));
  // A store opened before the list knew it (just added) still names itself in Conversations.
  const summaries = stores.some((entry) => entry.id === store.id)
    ? stores
    : [...stores, summaryOf(store)];
  // Conversations comes first once a store has just gone live on the shop's own pages, not on
  // every change to a live one; until the line is there, Install stays the next step.
  const onUpdate = (next: StoreDetail) => {
    remote.refresh();
    onChanged();
    const onShop = next.install.domains.some((domain) => domain.lineFound);
    if (next.status === 'live' && store.status !== 'live' && onShop) setTab('conversations');
  };
  return (
    <>
      <header className="sf-head">
        <span className="sf-crumb">
          {copy.pageTitle} / {copy.assistantTitle}
        </span>
        <div className="sf-head-row">
          <StoreMark store={store} />
          <h1>{store.name}</h1>
          <StoreSwitch stores={stores} current={store.id} onPick={onPick} onAdd={onAdd} />
          <StoreStatus store={store} />
          {/* Published but not on the shop's pages yet: putting it there is the next step. */}
          {store.status === 'live' && !onSite && !unchecked ? (
            active !== 'install' && (
              <button
                className="ws-button primary"
                data-testid="sf-add-to-site"
                onClick={() => setTab('install')}
              >
                {copy.addToSite}
              </button>
            )
          ) : store.status === 'live' ? (
            <button className="ws-button" data-testid="sf-ask-week" onClick={askWeek}>
              {marketName ? formatCopy(copy.askAbout, { scope: scopeName }) : copy.askWeek}
            </button>
          ) : null}
        </div>
      </header>
      <div className="sf-tabbar">
        <div className="sf-tabs ws-scroll-fade" role="tablist" aria-label={copy.assistantTitle}>
          {tabs.map((name) => (
            <button
              key={name}
              id={`sf-tab-${name}`}
              role="tab"
              className="sf-tab"
              aria-selected={active === name}
              tabIndex={active === name ? 0 : -1}
              aria-controls="sf-tabpanel"
              onClick={() => setTab(name)}
              onKeyDown={onTabKey}
            >
              {label[name]}
            </button>
          ))}
        </div>
        {markets.length > 0 && (active === 'overview' || active === 'conversations') && (
          <MarketFilter markets={markets} value={scope.market} onChange={onMarket} />
        )}
      </div>
      <div
        id="sf-tabpanel"
        role="tabpanel"
        aria-labelledby={`sf-tab-${active}`}
        className="sf-panel"
      >
        {active === 'overview' && (
          <OverviewTab
            scope={scope}
            stores={summaries}
            onFocus={(next) =>
              next.store === store.id ? onMarket(next.market) : onPick(next.store, next.market)
            }
          />
        )}
        {active === 'setup' && (
          <SetupTab
            store={store}
            onChanged={onUpdate}
            onTryWidget={() => setTab('widget')}
            onDesign={design}
            onKnowledge={knowledge}
            onProfile={profile}
            onPages={pages}
            onUpdatePages={updatePages}
            onMarket={addMarket}
            onFixRead={fixRead}
            notify={notify}
          />
        )}
        {active === 'widget' && <WidgetTab store={store} onDesign={design} />}
        {active === 'install' && (
          <InstallTab store={store} onChanged={onUpdate} notify={notify} onFix={ask} />
        )}
        {active === 'conversations' && (
          <ConversationsTab scope={scope} stores={summaries} onAsk={askAbout} />
        )}
      </div>
    </>
  );
}

/** Every assistant at once: the week across them, and every conversation, by market if asked. */
function AllView({
  stores,
  market,
  onMarket,
  tab: placeTab,
  onTab: setTab,
  onPick,
  onAdd,
}: {
  stores: StoreSummary[];
  market: string;
  onMarket: (code: string) => void;
  tab: string | null;
  onTab: (tab: Tab) => void;
  onPick: (id: string, market?: string) => void;
  onAdd: () => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const ws = useWorkspace();
  const tab: 'overview' | 'conversations' =
    placeTab === 'conversations' ? 'conversations' : 'overview';
  // The markets across stores, once each: a country's sites of different stores share its code.
  const markets = uniqueMarkets(stores);
  const scope: OverviewScope = {
    store: 'all',
    market: markets.some((entry) => entry.code === market) ? market : 'all',
  };
  const marketName = markets.find((entry) => entry.code === scope.market)?.name ?? null;
  const sites = stores.reduce((total, store) => total + Math.max(1, store.markets?.length ?? 0), 0);
  const askAbout = ({ text, reference }: AskChat) =>
    ws.send(text, { ...emptyTurnContext(), storefrontConversations: reference }, false, null, {
      newChat: true,
    });
  const marketPicked = markets.find((entry) => entry.code === scope.market) ?? null;
  const askWeek = () => askAbout(weekAsk(copy, scope, copy.allAssistants, marketPicked));
  const tabs = ['overview', 'conversations'] as const;
  // Arrow keys move between the tabs, as on one assistant's page.
  const onTabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next = tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length];
    setTab(next);
    requestAnimationFrame(() => document.getElementById(`sf-tab-${next}`)?.focus());
  };
  return (
    <>
      <header className="sf-head">
        <span className="sf-crumb">{copy.pageTitle}</span>
        <div className="sf-head-row">
          <span className="sf-mark sf-mark-all" aria-hidden="true">
            <WorkspaceIcon name="store" size={18} />
          </span>
          <h1>{copy.allAssistants}</h1>
          <StoreSwitch stores={stores} current="all" onPick={onPick} onAdd={onAdd} />
          <span className="sf-head-note">
            {/* Sites only when there are more of them than assistants: one each says nothing. */}
            {formatCopy(sites > stores.length ? copy.allAssistantsNote : copy.allAssistantsCount, {
              assistants: String(stores.length),
              sites: String(sites),
            })}
          </span>
          {stores.some((store) => store.status === 'live') && (
            <button className="ws-button" data-testid="sf-ask-week" onClick={askWeek}>
              {marketName
                ? formatCopy(copy.askAbout, {
                    scope: formatCopy(copy.scopeIn, {
                      assistant: copy.allAssistants,
                      market: marketName,
                    }),
                  })
                : copy.askWeek}
            </button>
          )}
        </div>
      </header>
      <div className="sf-tabbar">
        <div className="sf-tabs" role="tablist" aria-label={copy.allAssistants}>
          {tabs.map((name) => (
            <button
              key={name}
              id={`sf-tab-${name}`}
              role="tab"
              className="sf-tab"
              aria-selected={tab === name}
              tabIndex={tab === name ? 0 : -1}
              aria-controls="sf-tabpanel"
              onClick={() => setTab(name)}
              onKeyDown={onTabKey}
            >
              {name === 'overview' ? copy.tabOverview : copy.tabConversations}
            </button>
          ))}
        </div>
        {markets.length > 1 && (
          <MarketFilter markets={markets} value={scope.market} onChange={onMarket} />
        )}
      </div>
      <div id="sf-tabpanel" role="tabpanel" aria-labelledby={`sf-tab-${tab}`} className="sf-panel">
        {tab === 'overview' ? (
          <OverviewTab
            scope={scope}
            stores={stores}
            onFocus={(next) => onPick(next.store, next.market)}
          />
        ) : (
          <ConversationsTab scope={scope} stores={stores} onAsk={askAbout} />
        )}
      </div>
    </>
  );
}

function uniqueMarkets(stores: StoreSummary[]): StoreMarket[] {
  const seen = new Map<string, StoreMarket>();
  for (const store of stores)
    for (const market of store.markets ?? [])
      if (!seen.has(market.code)) seen.set(market.code, market);
  return [...seen.values()];
}

const summaryOf = (store: StoreDetail): StoreSummary => ({
  id: store.id,
  name: store.name,
  domain: store.domain,
  status: store.status,
  live: store.live ? { version: store.live.version, since: store.live.since } : null,
  ...(store.markets ? { markets: store.markets } : {}),
});

function StoreMark({ store }: { store: StoreDetail }) {
  const logo = store.found?.look.logo;
  // A wordmark gets a plate as wide as it needs; a square logo keeps the round mark. The plate is
  // the colour the logo sits on in the shop's header when reading the store saw it, else the
  // store's own colour.
  const [wide, setWide] = useState(false);
  const plate = store.found?.look.logoBackground ?? store.found?.look.colours[0];
  return (
    <span
      className="sf-mark"
      data-wide={wide || undefined}
      style={logo && plate ? { background: plate } : undefined}
      aria-hidden="true"
    >
      {logo ? (
        <img
          src={logo}
          alt=""
          onLoad={(event) =>
            setWide(event.currentTarget.naturalWidth > event.currentTarget.naturalHeight * 1.6)
          }
        />
      ) : (
        store.name.slice(0, 1).toUpperCase()
      )}
    </span>
  );
}

/** The company's stores, one at a time: switch between them or add another. */
function StoreSwitch({
  stores,
  current,
  onPick,
  onAdd,
}: {
  stores: StoreSummary[];
  /** A store's id, or `all`. */
  current: string;
  onPick: (id: string) => void;
  onAdd: () => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const sites = stores.reduce((total, store) => total + Math.max(1, store.markets?.length ?? 0), 0);
  // Published assistants first, then ready, still reading and stopped; by name inside each.
  const ordered = [...stores].sort(
    (a, b) =>
      (STATUS_ORDER[a.status] ?? 4) - (STATUS_ORDER[b.status] ?? 4) || a.name.localeCompare(b.name),
  );
  return (
    <Menu>
      <MenuTrigger
        className="sf-store-switch"
        aria-label={copy.switchStore}
        data-testid="sf-store-switch"
      >
        <WorkspaceIcon name="chevron" size={16} />
      </MenuTrigger>
      <MenuContent label={copy.storesLabel} align="start" className="sf-store-menu">
        {/* Adding comes first: with many stores, the end of the list is out of view. */}
        <MenuItem icon={<WorkspaceIcon name="plus" size={16} />} onSelect={onAdd}>
          {copy.addStore}
        </MenuItem>
        {stores.length > 1 && <MenuSeparator />}
        {stores.length > 1 && (
          <>
            <MenuLabel>{copy.storesLabel}</MenuLabel>
            <MenuRadioGroup value={current} onValueChange={onPick}>
              <MenuRadioItem
                value="all"
                description={formatCopy(
                  sites > stores.length ? copy.allAssistantsNote : copy.allAssistantsCount,
                  { assistants: String(stores.length), sites: String(sites) },
                )}
              >
                {copy.allAssistants}
              </MenuRadioItem>
              <MenuSeparator />
              {ordered.map((store) => {
                // A store read for the first time is still named by its domain: say it once.
                const description = storeLine(store, copy);
                return (
                  <MenuRadioItem key={store.id} value={store.id} description={description}>
                    {store.name}
                  </MenuRadioItem>
                );
              })}
            </MenuRadioGroup>
          </>
        )}
      </MenuContent>
    </Menu>
  );
}

const STATUS_ORDER: Record<string, number> = { live: 0, ready: 1, reading: 2, failed: 3 };

export function StoreStatus({ store }: { store: StoreDetail }) {
  const copy = useNamespaceCopy(storefrontCopy);
  if (store.status === 'live' && store.live) {
    // Live means shoppers meet it: a version is published and the line is on the store's pages.
    // Published without the line is not live yet, whatever a test on our side loaded.
    const site = store.install.domains.find((domain) => domain.lineFound)?.host ?? null;
    const version = String(store.live.version);
    // A site that blocks automated visits could not be checked: neither on it nor missing from it.
    const published = siteUnchecked(store.install.domains)
      ? copy.statusPublishedUnverified
      : copy.statusPublished;
    return site ? (
      <StatusPill tone="live">{formatCopy(copy.statusLive, { version, domain: site })}</StatusPill>
    ) : (
      <StatusPill tone="neutral">{formatCopy(published, { version })}</StatusPill>
    );
  }
  // A read that stopped leaves a saved version as it was: the assistant is still ready to try. The
  // server keeps such a store ready; a stopped run says the last read did not finish.
  const stopped = Boolean(store.run && ['failed', 'cancelled', 'held'].includes(store.run.status));
  if ((store.status === 'ready' && stopped) || (store.status === 'failed' && store.draft))
    return <StatusPill tone="warning">{copy.statusReadStopped}</StatusPill>;
  if (store.status === 'ready') return <StatusPill tone="success">{copy.statusReady}</StatusPill>;
  if (store.status === 'failed') return <StatusPill tone="danger">{copy.statusFailed}</StatusPill>;
  // A read of one part says which ("Reading the products again"); a whole read reads the store.
  return (
    <StatusPill tone="neutral">
      {runScopeText(store.run, store.markets, copy) ?? copy.statusReading}
    </StatusPill>
  );
}
